import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useBooking } from '../../contexts/BookingContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuthPrompt } from '../../hooks/useAuthPrompt';
import Logo from '../Logo';
import CartDrawer from '../CartDrawer';
import {
  Home, Search, User, Bell, MessageCircle,
  ShoppingBag, Heart,
  Map, LogIn, Briefcase, ChevronLeft,
  Instagram,
} from 'lucide-react';

const WebsiteLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userData, logout } = useAuth();
  const { cartCount, openCart } = useCart();
  const { pendingBookings } = useBooking();
  const { unreadCount } = useNotifications();
  const { requireAuth } = useAuthPrompt();
  const [showNotifications] = useState(false);

  const isSignedIn = !!user;
  const totalBagCount = cartCount + (pendingBookings?.length || 0);
  const isMapPage = location.pathname === '/dashboard/map';

  const desktopNavItems = [
    { path: '/dashboard', label: 'Home', requiresAuth: false },
    { path: '/dashboard/calendar', label: 'Calendar', requiresAuth: false },
    { path: '/dashboard/search', label: 'Discover', requiresAuth: false },
  ];

  const mobileNavItems = [
    { path: '/dashboard', icon: <Home size={22} />, label: 'Home', requiresAuth: false },
    { path: '/dashboard/search', icon: <Search size={22} />, label: 'Discover', requiresAuth: false },
    { path: '/dashboard/map', icon: <Map size={22} />, label: 'Map', requiresAuth: true },
    { path: '/dashboard/chat', icon: <MessageCircle size={22} />, label: 'Chat', requiresAuth: false },
    { path: '/dashboard/profile', icon: <User size={22} />, label: 'Profile', requiresAuth: true },
  ];

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/dashboard' || location.pathname === '/';

  const goTo = (path, { requiresAuth: authRequired = false, action = 'profile' } = {}) => {
    if (authRequired && !isSignedIn) {
      requireAuth(action, () => navigate(path));
      return;
    }
    navigate(path);
  };

  const goPro = () => {
    navigate('/register', { state: { role: 'professional' } });
  };

  const handleLogout = async () => {
    try {
      const isMobile = window.innerWidth < 768;
      await logout();
      if (isMobile) navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getPageTitle = () => {
    if (location.pathname === '/dashboard/search') return 'Discover';
    if (location.pathname === '/dashboard/chat') return 'Messages';
    if (location.pathname === '/dashboard/map') return 'Map';
    if (location.pathname === '/dashboard/profile') return 'Profile';
    if (location.pathname === '/dashboard/favorites') return 'Favorites';
    if (location.pathname === '/dashboard/calendar') return 'Calendar';
    if (location.pathname === '/dashboard/notifications') return 'Notifications';
    return '';
  };

  // ---------- Social handles (all skytone.za) ----------
  const SOCIALS = [
    {
      label: 'Instagram',
      href: 'https://instagram.com/skytone.za',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-4 h-4">
          <rect x="3" y="3" width="18" height="18" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
        </svg>
      ),
    },
    {
      label: 'TikTok',
      href: 'https://tiktok.com/@skytone.za',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M16.5 3c.3 2 1.6 3.4 3.5 3.7v2.6c-1.3.1-2.6-.3-3.6-1.1v6.1c0 3.3-2.7 5.7-5.8 5.7S4.8 17.6 4.8 14.3c0-3.2 2.6-5.7 5.8-5.7.4 0 .8 0 1.1.1v2.7c-.3-.1-.7-.2-1.1-.2-1.8 0-3.1 1.4-3.1 3.1 0 1.8 1.4 3.1 3.1 3.1s3.1-1.4 3.1-3.1V3h2.8z" />
        </svg>
      ),
    },
    {
      label: 'Facebook',
      href: 'https://facebook.com/skytone.za',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M13.5 22v-8h2.7l.4-3.1h-3.1V8.9c0-.9.3-1.5 1.5-1.5h1.6V4.6c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.4-4 4.1v2.3H7.5V14h2.7v8h3.3z" />
        </svg>
      ),
    },
    {
      label: 'Pinterest',
      href: 'https://pinterest.com/skytone.za',
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
          <path d="M12 2C6.5 2 2 6.5 2 12c0 4.2 2.6 7.9 6.3 9.4-.1-.8-.2-2 0-2.9.2-.9 1.4-5.8 1.4-5.8s-.4-.7-.4-1.8c0-1.7 1-2.9 2.2-2.9 1 0 1.5.8 1.5 1.7 0 1-.6 2.5-1 3.9-.3 1.1.6 2.1 1.8 2.1 2.1 0 3.6-2.7 3.6-5.8 0-2.4-1.6-4.2-4.6-4.2-3.3 0-5.4 2.5-5.4 5.2 0 .9.3 1.5.7 2 .2.2.2.3.1.5-.1.2-.2.8-.2 1-.1.3-.3.4-.6.3-1.6-.7-2.4-2.5-2.4-4.5 0-3.3 2.8-7.3 8.4-7.3 4.5 0 7.5 3.3 7.5 6.8 0 4.6-2.6 8.1-6.5 8.1-1.3 0-2.5-.7-2.9-1.5 0 0-.7 2.8-.9 3.4-.3 1.1-1 2.4-1.6 3.3C9.9 21.8 10.9 22 12 22c5.5 0 10-4.5 10-10S17.5 2 12 2z" />
        </svg>
      ),
    },
  ];

  const FOOTER_LINKS = [
    {
      title: 'Discover',
      links: [
        { label: 'All Services', path: '/dashboard/discover/all' },
        { label: 'Hair', path: '/dashboard/discover/hair' },
        { label: 'Nails', path: '/dashboard/discover/nails' },
        { label: 'Skin', path: '/dashboard/discover/skin' },
        { label: 'Lashes', path: '/dashboard/discover/lashes' },
        { label: 'Spa', path: '/dashboard/discover/spa' },
      ],
    },
    {
      title: 'Account',
      links: [
        { label: 'Profile', path: '/dashboard/profile' },
        { label: 'Calendar', path: '/dashboard/calendar' },
        { label: 'Favorites', path: '/dashboard/favorites' },
        { label: 'Messages', path: '/dashboard/chat' },
      ],
    },
    {
      title: 'Company',
      links: [
        { label: 'Go Pro', path: '/register' },
        { label: 'Log In', path: '/login' },
      ],
    },
  ];

  return (
    <>
      <div className="min-h-screen bg-[#FCF8F9] flex flex-col">
        {/* ============================================================ */}
        {/* DESKTOP NAVBAR                                               */}
        {/* ============================================================ */}
        <div className="hidden md:flex justify-center pt-4 px-4 pointer-events-none">
          <header className="pointer-events-auto w-full max-w-6xl bg-white/70 backdrop-blur-xl border border-white/30 shadow-lg rounded-full px-6 py-2">
            <div className="flex items-center justify-between h-14">
              <div className="cursor-pointer shrink-0" onClick={() => navigate('/dashboard')}>
                <Logo size={100} className="text-[#EFA9B6]" />
              </div>

              <nav className="flex items-center gap-1 flex-1 justify-center">
                {desktopNavItems.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => goTo(item.path, {
                      requiresAuth: item.requiresAuth,
                      action: 'profile',
                    })}
                    className={`px-4 py-2 rounded-full transition-all duration-300 text-sm font-medium ${
                      isActive(item.path)
                        ? 'bg-[#FFF5F6] text-[#EFA9B6] shadow-sm'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-white/30'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>

              <div className="flex items-center gap-1 shrink-0">
                {isSignedIn && (
                  <>
                    {/* MAP — signed-in only */}
                    <button
                      type="button"
                      onClick={() => goTo('/dashboard/map', {
                        requiresAuth: true,
                        action: 'profile',
                      })}
                      className={`p-2 rounded-full transition-all duration-300 ${
                        isActive('/dashboard/map')
                          ? 'bg-[#FFF5F6] text-[#EFA9B6] shadow-sm'
                          : 'text-gray-400 hover:text-gray-600 hover:bg-white/30'
                      }`}
                      title="Map"
                    >
                      <Map size={18} />
                    </button>

                    <button
                      type="button"
                      onClick={() => navigate('/dashboard/chat')}
                      className={`p-2 rounded-full transition-all duration-300 ${
                        isActive('/dashboard/chat')
                          ? 'bg-[#FFF5F6] text-[#EFA9B6] shadow-sm'
                          : 'text-gray-400 hover:text-gray-600 hover:bg-white/30'
                      }`}
                    >
                      <MessageCircle size={18} />
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard/favorites')}
                      className={`p-2 rounded-full transition-all duration-300 ${
                        isActive('/dashboard/favorites')
                          ? 'bg-[#FFF5F6] text-[#EFA9B6] shadow-sm'
                          : 'text-gray-400 hover:text-gray-600 hover:bg-white/30'
                      }`}
                    >
                      <Heart size={18} />
                    </button>
                  </>
                )}

                {/* CART — always visible */}
                <button
                  type="button"
                  onClick={openCart}
                  className="relative p-2 hover:bg-white/50 rounded-full transition-colors"
                >
                  <ShoppingBag size={20} className="text-gray-600" />
                  {totalBagCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#EFA9B6] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                      {totalBagCount > 9 ? '9+' : totalBagCount}
                    </span>
                  )}
                </button>

                {/* BELL — signed-in only */}
                {isSignedIn && (
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard/notifications')}
                    className={`relative p-2 rounded-full transition-all duration-300 ${
                      isActive('/dashboard/notifications')
                        ? 'bg-[#FFF5F6] text-[#EFA9B6] shadow-sm'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-white/30'
                    }`}
                  >
                    <Bell size={20} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                )}

                {isSignedIn ? (
                  <>
                    <button
                      type="button"
                      onClick={() => navigate('/dashboard/profile')}
                      className="flex items-center gap-2 pl-2 border-l border-gray-200/50"
                    >
                      <div className="w-8 h-8 rounded-full bg-[#FFF5F6] flex items-center justify-center text-[#EFA9B6]">
                        <User size={16} />
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {userData?.name || 'User'}
                      </span>
                    </button>

                    <button
                      type="button"
                      onClick={handleLogout}
                      className="text-sm text-gray-400 hover:text-red-500 transition-colors ml-2"
                    >
                      Sign Out
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors flex items-center gap-1.5"
                    >
                      <LogIn size={15} />
                      Log In
                    </button>
                    <button
                      type="button"
                      onClick={goPro}
                      className="px-5 py-2 bg-[#8B5FBF] text-white rounded-full text-sm font-bold hover:bg-[#7A4EAD] transition-colors flex items-center gap-1.5 shadow-sm"
                    >
                      <Briefcase size={15} />
                      Go Pro
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>
        </div>

        {/* ============================================================ */}
        {/* MOBILE HEADER                                                */}
        {/* ============================================================ */}
        <header
          className={`md:hidden sticky top-0 z-30 bg-[#FCF8F9]/80 backdrop-blur-xl px-6 ${
            isMapPage ? 'pt-3 pb-3' : 'pt-10 pb-6'
          }`}
        >
          <div className="flex justify-between items-center">
            {isMapPage ? (
              <div className="w-full flex items-center gap-2">
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-10 h-10 rounded-full bg-white shadow-sm flex items-center justify-center text-gray-700 hover:bg-gray-50 transition-colors"
                  aria-label="Back"
                >
                  <ChevronLeft size={20} />
                </button>
                <span className="font-serif font-bold text-lg text-gray-900">Map</span>
              </div>
            ) : isHomePage ? (
              <div className="w-full flex justify-center">
                <Logo size={100} className="text-[#EFA9B6]" />
              </div>
            ) : (
              <div className="w-full flex justify-center">
                <span className="font-serif font-bold text-lg text-gray-900">
                  {getPageTitle()}
                </span>
              </div>
            )}

            <div className="absolute right-6 flex items-center gap-2">
              {/* Bell — signed-in only */}
              {isSignedIn && (
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/notifications')}
                  className="relative w-11 h-11 bg-[#FFF5F6] text-[#EFA9B6] rounded-2xl flex items-center justify-center hover:bg-[#FBE1E8] transition-colors shadow-sm shrink-0"
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center border-2 border-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              )}

              {/* Cart — always visible */}
              <button
                type="button"
                onClick={openCart}
                className="relative w-11 h-11 bg-[#FFF5F6] text-[#EFA9B6] rounded-2xl flex items-center justify-center hover:bg-[#FBE1E8] transition-colors shadow-sm shrink-0"
              >
                <ShoppingBag size={20} />
                {totalBagCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#EFA9B6] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {totalBagCount > 9 ? '9+' : totalBagCount}
                  </span>
                )}
              </button>

              {/* Go Pro — guests only */}
              {!isSignedIn && (
                <button
                  type="button"
                  onClick={goPro}
                  className="px-4 py-2 bg-[#8B5FBF] text-white rounded-full text-xs font-bold hover:bg-[#7A4EAD] transition-colors shrink-0 shadow-sm"
                >
                  Go Pro
                </button>
              )}
            </div>
          </div>
        </header>

        {/* ============================================================ */}
        {/* PAGE CONTENT — no padding on map page                        */}
        {/* ============================================================ */}
        <main
          className={
            isMapPage
              ? 'w-full flex-1'
              : 'max-w-7xl mx-auto px-4 lg:px-8 py-6 lg:pb-6 w-full flex-1'
          }
          style={
            isMapPage
              ? { paddingBottom: 0 }
              : { paddingBottom: 'calc(8rem + env(safe-area-inset-bottom))' }
          }
        >
          {children}
        </main>

        {/* ============================================================ */}
        {/* DESKTOP-ONLY FOOTER                                          */}
        {/* ============================================================ */}
        <footer className="hidden md:block bg-white border-t border-gray-100 mt-12">
          <div className="max-w-6xl mx-auto px-8 py-12">
            <div className="grid grid-cols-12 gap-8">
              {/* Brand + socials */}
              <div className="col-span-5">
                <div className="cursor-pointer inline-block mb-4" onClick={() => navigate('/dashboard')}>
                  <Logo size={120} className="text-[#EFA9B6]" />
                </div>
                <p className="text-sm text-gray-500 max-w-xs leading-relaxed mb-6">
                  Your beauty journey, in one place. Discover, book and shop from trusted professionals near you.
                </p>
                <div className="flex items-center gap-2">
                  {SOCIALS.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={s.label}
                      aria-label={s.label}
                      className="w-9 h-9 rounded-full bg-[#FFF5F6] text-[#EFA9B6] flex items-center justify-center hover:bg-[#FBE1E8] hover:scale-105 transition-all"
                    >
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>

              {/* Link columns */}
              {FOOTER_LINKS.map((col) => (
                <div key={col.title} className="col-span-2">
                  <h3 className="text-[11px] font-bold text-gray-900 uppercase tracking-[0.15em] mb-4">
                    {col.title}
                  </h3>
                  <ul className="space-y-2.5">
                    {col.links.map((link) => (
                      <li key={link.label}>
                        <button
                          type="button"
                          onClick={() => navigate(link.path)}
                          className="text-sm text-gray-500 hover:text-[#EFA9B6] transition-colors text-left"
                        >
                          {link.label}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {/* Newsletter / CTA */}
              <div className="col-span-1" />
            </div>

            <div className="mt-12 pt-6 border-t border-gray-100 flex items-center justify-between">
              <p className="text-xs text-gray-400">
                © {new Date().getFullYear()} Skytone. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com/skytone.za"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-400 hover:text-[#EFA9B6] transition-colors"
                >
                  @skytone.za
                </a>
                <span className="text-gray-200">·</span>
                <span className="text-xs text-gray-400">Made with care</span>
              </div>
            </div>
          </div>
        </footer>

        {/* ============================================================ */}
        {/* MOBILE BOTTOM NAV — hidden on map page                       */}
        {/* ============================================================ */}
        {!isMapPage && (
          <nav
            className="md:hidden fixed left-1/2 -translate-x-1/2 z-40 w-[90%] max-w-[340px] pointer-events-none"
            style={{
              bottom: 'calc(1.5rem + env(safe-area-inset-bottom))',
            }}
          >
            <div className="bg-white/70 backdrop-blur-xl border border-white/40 shadow-[0_8px_32px_rgba(0,0,0,0.12)] rounded-full p-2 flex justify-between items-center px-4 pointer-events-auto">
              {mobileNavItems.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => goTo(item.path, {
                    requiresAuth: item.requiresAuth,
                    action: 'profile',
                  })}
                  className={`p-2.5 rounded-full transition-all duration-300 ${
                    isActive(item.path)
                      ? 'bg-[#FFF5F6] text-[#EFA9B6] shadow-sm'
                      : 'text-gray-400 hover:text-gray-600 hover:bg-white/30'
                  }`}
                  style={{ touchAction: 'manipulation' }}
                >
                  {item.icon}
                </button>
              ))}
            </div>
          </nav>
        )}
      </div>

      <CartDrawer />
    </>
  );
};

export default WebsiteLayout;